// ─── Standalone-link extraction ──────────────────────────────────────
//
// CMS rich-text often ends with a "call-to-action" link sitting alone on its
// own line, e.g. `<div><a href="…">VIEW OUR PACKAGES</a></div>`, usually fenced
// by blank `<div><br></div>` spacer lines. This utility pulls those standalone
// links out so they can be rendered as buttons, and returns the remaining copy
// with the link block AND its hugging blank/<br> lines removed.
//
// Inline links inside a sentence are left untouched. Pure string work → safe to
// run on the server and the client (no DOMParser, no hydration drift).

export interface ExtractedLink {
  href: string;
  label: string;
}

export interface SplitHtmlLinksResult {
  /** HTML with the standalone link blocks (and adjacent blank lines) removed. */
  html: string;
  /** Standalone links pulled out, in document order. */
  links: ExtractedLink[];
}

const INLINE_TAGS = "b|i|u|s|em|strong|span|font|small|sub|sup|mark";

// A <div>/<p> (optionally with attributes) whose only content is a single <a>
// — allowing trailing inline-close tags / whitespace / &nbsp;.
// Groups: 1 = block tag (for the closing backreference), 2 = href, 3 = inner.
const LINK_BLOCK_SRC =
  `<(div|p)(?:\\s[^>]*)?>\\s*<a\\b[^>]*?\\shref="([^"]*)"[^>]*>([\\s\\S]*?)<\\/a>` +
  `(?:\\s|&nbsp;|<\\/(?:${INLINE_TAGS})>)*<\\/\\1>`;

// A "blank" block: <div><br></div>, <div><b><br></b></div>, <p></p>, etc.
const BLANK_BLOCK_SRC =
  `<(?:div|p)(?:\\s[^>]*)?>\\s*(?:<(?:${INLINE_TAGS})[^>]*>\\s*)*` +
  `(?:<br\\s*\\/?>\\s*)?(?:<\\/(?:${INLINE_TAGS})>\\s*)*<\\/(?:div|p)>`;

function fromCodePointSafe(code: number): string {
  try {
    return Number.isFinite(code) && code > 0 ? String.fromCodePoint(code) : "";
  } catch {
    return "";
  }
}

function decodeEntities(value: string): string {
  return (
    value
      // Numeric (decimal & hex) entities — e.g. &#160; / &#xA0; (nbsp).
      .replace(/&#x([0-9a-f]+);/gi, (_, hex) =>
        fromCodePointSafe(parseInt(hex, 16)),
      )
      .replace(/&#(\d+);/g, (_, dec) => fromCodePointSafe(parseInt(dec, 10)))
      // Named entities.
      .replace(/&nbsp;/gi, " ")
      .replace(/&lt;/gi, "<")
      .replace(/&gt;/gi, ">")
      .replace(/&quot;/gi, '"')
      .replace(/&apos;/gi, "'")
      // Ampersand last so we never synthesize a new entity from the above.
      .replace(/&amp;/gi, "&")
  );
}

function stripTags(value: string): string {
  return value.replace(/<[^>]+>/g, "");
}

/**
 * Extract standalone CTA links from CMS HTML.
 *
 * @returns `links` (href + plain-text label, target-blank to be applied by the
 *   caller) and `html` — the original copy with those link blocks and their
 *   adjacent blank/<br> lines stripped out so it reads cleanly.
 */
export function extractStandaloneLinks(
  html: string | null | undefined,
): SplitHtmlLinksResult {
  const source = html?.trim() ?? "";
  if (!source) return { html: "", links: [] };

  const links: ExtractedLink[] = [];
  const collector = new RegExp(LINK_BLOCK_SRC, "gi");
  let match: RegExpExecArray | null;
  while ((match = collector.exec(source)) !== null) {
    const href = decodeEntities(match[2].trim());
    const label = decodeEntities(stripTags(match[3]))
      .replace(/\s+/g, " ")
      .trim();
    if (href) links.push({ href, label: label || "Learn more" });
  }

  if (links.length === 0) return { html: source, links: [] };

  // Remove each standalone link block together with any blank/<br> blocks that
  // hug it on either side, so no empty gap is left behind.
  const removal = new RegExp(
    `(?:${BLANK_BLOCK_SRC}\\s*)*(?:${LINK_BLOCK_SRC})(?:\\s*${BLANK_BLOCK_SRC})*`,
    "gi",
  );
  const cleaned = source
    .replace(removal, "")
    .replace(/>\s+</g, "><")
    .trim();

  return { html: cleaned, links };
}
